/**
 * @module acng/messenger/factory/messenger
 */
const ngFactoryName = 'messenger';
export default ngFactoryName;
export const messengerFactory = ['sock', 'Dialog', '$rootScope', 'Amateur', factory];

import {RootScope} from 'acng/zz-app';
import {Message, MessageData} from '../model/message';
import {createAudio} from '@acng/frontend-bounty';
import {Dialog as IDialog} from './dialog';
import {amateurVisitsUser} from 'acng/amateurPool/service/profile-visit.js';
import {AmateurConstructor} from 'acng/amateurPool/factory/Amateur';

type DialogData = {
  partner: string;
  read: boolean;
  time: number;
};

export interface IMessenger {
  isNew(target: string | IDialog): boolean;
  get newMessagesCount(): number;
  get(id: string, pushToContacts?: boolean): IDialog;
  online: boolean;
  dialogs: IDialog[] & {changed?: number};
}

function factory(
  sock: core.Sock, //
  Dialog: import('./dialog').DialogConstructor,
  $rootScope: RootScope,
  Amateur: AmateurConstructor,
) {
  class Messenger implements IMessenger {
    #byAmateur = new Map();
    #dialogs: IDialog[] & {changed?: number} = [];
    #online = false;
    #newMessagesCount = 0;

    constructor() {
      if (sock.online()) {
        this.#onConnect();
      }
      this.#registerSocketListeners();
      this.#dialogs.changed = 0;
    }

    get online() {
      return this.#online;
    }

    get dialogs() {
      return this.#dialogs;
    }

    get(id: string, pushToContacts = false) {
      let dialog = this.#dialogs.find(d => d.id == id);
      if (!dialog) {
        dialog = new Dialog(id);
        this.#byAmateur.set(id, dialog);
        if (pushToContacts) {
          this.#dialogs.push(dialog);
          this.#sort();
        }
      }
      return dialog;
    }

    /**
     * `true` when dialog is flagged "new", `false` otherwise
     */
    isNew(target: string | IDialog) {
      let id;
      if (target instanceof Dialog) {
        id = target.id;
      } else {
        id = target;
      }
      return this.#byAmateur.get(id)?.new;
    }

    get newMessagesCount() {
      return this.#newMessagesCount;
    }

    dialogIsOpen(amateurId: number) {
      return !!this.#byAmateur.get(amateurId)?.open;
    }

    #registerSocketListeners() {
      sock.addEventListener('open', () => this.#onConnect());
      sock.addEventListener('close', () => this.#onDisconnect());
      sock.registerHandler('dialog.close', dialogId => this.#markAsClosed(dialogId));
      sock.registerHandler('dialog.read', dialogId => this.#markAsRead(dialogId));
      sock.registerHandler('message.sent', data => this.#onMessageSent(data));
      sock.registerHandler('message.received', data => this.#onMessageReceived(data));
    }

    async #onConnect() {
      this.#online = true;
      this.#newMessagesCount = 0;
      for (const data of (await sock.send('dialogs.get', null)) as DialogData[]) {
        const amateurId = data.partner;
        let dialog = this.#dialogs.find(d => d.id == amateurId);
        if (!dialog) {
          dialog = new Dialog(amateurId);
          this.#byAmateur.set(amateurId, dialog);
          this.#dialogs.push(dialog);
        }
        dialog.lastActivity = new Date(data.time);
        dialog.new = !data.read;
        if (dialog.new) {
          this.#newMessagesCount++;
        }
      }
      this.#dialogs.changed!++;
      this.#sort();
    }

    #onDisconnect() {
      this.#online = false;
      this.#dialogs.length = 0;
      this.#dialogs.changed!++;
    }

    #markAsClosed(dialogId: string) {
      const index = this.#dialogs.findIndex(dialog => dialog.id == dialogId);
      if (index >= 0) {
        this.#dialogs.splice(index, 1);
        this.#dialogs.changed!++;
        $rootScope.$digest();
      }
    }

    #markAsRead(dialogId: string) {
      const dialog = this.#dialogs.find(d => d.id == dialogId);
      if (dialog) {
        dialog.dispatchEvent(new CustomEvent('read'));
        if (dialog.new) {
          dialog.new = false;
          this.#newMessagesCount--;
        }
        $rootScope.$digest();
      }
    }

    #put(message: Message) {
      const dialog = this.get(message.getAmateurId(), true);
      if (message.isMine) {
        dialog.new = false;
        dialog.lastActivity = new Date();
        dialog.activated = dialog.lastActivity.getTime();
      } else {
        if (!dialog.new) {
          this.#newMessagesCount++;
        }
        dialog.new = true;
        dialog.lastActivity = new Date(message.time);
      }
      this.#sort();
      dialog.dispatchEvent(new CustomEvent('message', {detail: {message}}));
      return dialog;
    }

    #sort() {
      let changed = 0;
      this.#dialogs.sort((a, b) => {
        let diff = b.lastActivity.getTime() - a.lastActivity.getTime();
        if (diff < 0) {
          changed = 1;
        }
        return diff;
      });
      this.#dialogs.sort((a, b) => {
        let diff = b.activated - a.activated;
        if (diff < 0) {
          changed = 1;
        }
        return diff;
      });
      this.#dialogs.changed! += changed;
    }

    async #onMessageSent(data: MessageData) {
      const amateur = await Amateur.get(data.recipient);
      this.#put(new Message(amateur, data));
      $rootScope.$digest();
    }

    async #onMessageReceived(data: MessageData) {
      const amateur = await Amateur.get(data.sender);
      const message = new Message(amateur, data);
      const dialog = this.#put(message);
      if (message.payload.ibid) {
        amateurVisitsUser(message.sender);
      }
      if (dialog.activated) {
        createAudio('/sounds/testsound4.mp3');
      }
      $rootScope.$digest();
    }
  }

  return new Messenger();
}
